import { Alert, Button, Classes, Intent, Menu, MenuItem, Popover, PopoverPosition } from "@blueprintjs/core";
import classNames from "classnames";
import { JitsiConference, JitsiMeet } from "lib/lib-jitsi-meet";
import { action, computed, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { DataMessage, SpotlightUserMessage } from "../../chat";
import { ChatRequestStatus, ChatRoomState, ChatUIState, RemoteVideoLayoutOptions, RoomState } from "../../common";
import { shiftLayoutsAfterPinning, shiftLayoutsAfterUnpinning } from "../../video";
import { ChatRequestManager } from "../data";
import ChatRoom from "./ChatRoom";
import styles from "./RemoteParticipantDropdown.module.css";
import rglStyles from "./ResponsiveGridLayout.module.css";

export interface RemoteParticipantDropdownProps {
    jitsi: JitsiMeet;
    room: JitsiConference;
    roomState: RoomState;
    layout: RemoteVideoLayoutOptions;
    currentNumCols: number;
    userId: string;
    privateChats: ChatRoomState["private"];
}

@observer
export default class extends React.Component<RemoteParticipantDropdownProps> {
    @observable
    private showBlockConfirmDialog = false;

    @computed
    private get chatUIState(): ChatUIState | undefined {
        return this.props.privateChats.get(this.props.userId);
    }

    @computed
    private get participant() {
        return this.props.room.getParticipantById(this.props.userId);
    }

    @computed
    private get chatRequestStatus(): ChatRequestStatus | undefined {
        const { roomState, userId } = this.props;
        return roomState.privateChatRequests.get(userId);
    }

    private set chatRequestStatus(status: ChatRequestStatus | undefined) {
        if (status !== undefined) {
            this.props.roomState.privateChatRequests.set(this.props.userId, status);
        }
    }

    public render() {
        return (
            <>
                <Popover
                    className={styles.targetContainer}
                    popoverClassName={classNames(styles.popover, {
                        [styles.hasActiveChat]: this.chatUIState !== undefined,
                    })}
                    isOpen={this.props.layout.isDropdownOpen}
                    // make room so you can still see video
                    position={PopoverPosition.RIGHT_TOP}
                    content={this.renderDropdownContents()}
                    onInteraction={this.handleTargetInteraction}
                    hasBackdrop={true}
                >
                    <Button icon="more" small={true} minimal={true} />
                </Popover>
                {this.participant && <ChatRequestManager {...this.props} participant={this.participant} />}
            </>
        );
    }

    public renderDropdownContents() {
        const { room, roomState, userId } = this.props;
        const recipient = room.getParticipantById(userId);
        const status = this.chatRequestStatus;

        return (
            <div className={classNames(styles.container, rglStyles.notDraggable)}>
                {this.chatUIState !== undefined && (
                    <div className={styles.chatContainer}>
                        <ChatRoom
                            jitsi={this.props.jitsi}
                            room={room}
                            recipient={recipient}
                            uiState={this.chatUIState}
                            inputProps={{
                                disabled: status !== "incomingAccept" && status !== "outgoingAccept",
                                intent:
                                    status === "incomingBlock" || status === "outgoingBlock"
                                        ? Intent.DANGER
                                        : Intent.NONE,
                            }}
                        />
                    </div>
                )}
                <Menu>
                    {recipient ? this.renderChatMenuItem() : undefined}
                    <MenuItem
                        text={this.props.layout.isPinned ? "Unpin" : "Pin"}
                        icon={this.props.layout.isPinned ? "unpin" : "pin"}
                        onClick={this.handleRemoteParticipantPin}
                    />
                    {roomState.isHost && recipient ? this.renderSpotlightItem() : undefined}
                    <MenuItem text="Reset video size" icon="grid-view" onClick={this.handleResetVideoSize} />
                </Menu>
                {this.maybeRenderBlockAlert()}
            </div>
        );
    }

    private renderChatMenuItem() {
        switch (this.chatRequestStatus) {
            case "incomingAccept":
            case "outgoingAccept":
                return (
                    <MenuItem
                        text="Block private chat"
                        icon="blocked-person"
                        intent={Intent.DANGER}
                        onClick={this.handleBlockPrivateChat}
                        shouldDismissPopover={false}
                    />
                );
            case "incomingBlock":
                return (
                    <MenuItem
                        text="Blocked from private chat"
                        icon="blocked-person"
                        intent={Intent.DANGER}
                        disabled={true}
                    />
                );
            case "incomingRequest":
                return (
                    <MenuItem
                        text="Accept chat request?"
                        icon="tick"
                        intent={Intent.PRIMARY}
                        onClick={this.handleAcceptPrivateChat}
                        shouldDismissPopover={false}
                    />
                );
            case "outgoingBlock":
                return (
                    <MenuItem
                        text="You have blocked this user"
                        icon="blocked-person"
                        intent={Intent.PRIMARY}
                        disabled={true}
                    />
                );
            case "outgoingRequest":
                return <MenuItem icon="chat" text="Requested private chat..." disabled={true} />;
            default:
                return (
                    <MenuItem
                        text="Request private chat"
                        icon="chat"
                        intent={Intent.SUCCESS}
                        onClick={this.handleRequestPrivateChat}
                        shouldDismissPopover={false}
                    />
                );
        }
    }

    private renderSpotlightItem() {
        const { roomState, userId } = this.props;
        const isSpotlighted = roomState.spotlightUserId === userId;

        return (
            <MenuItem
                text={isSpotlighted ? "End spotlight" : "Spotlight video"}
                onClick={this.handleSpotlightToggle}
                icon="star"
            />
        );
    }

    private maybeRenderBlockAlert() {
        if (this.participant !== undefined) {
            return (
                <Alert
                    className={Classes.DARK}
                    icon="cross"
                    intent={Intent.DANGER}
                    isOpen={this.showBlockConfirmDialog}
                    onConfirm={this.handleBlockConfirm}
                    onCancel={this.handleBlockCancel}
                    confirmButtonText="Block"
                    cancelButtonText="Cancel"
                    canEscapeKeyCancel={true}
                    canOutsideClickCancel={true}
                >
                    Block {this.participant.getDisplayName()}? This cannot be undone.
                </Alert>
            );
        }
    }

    @action
    private handleSpotlightToggle = () => {
        const { room, roomState, userId } = this.props;
        const isSpotlighted = roomState.spotlightUserId === userId;
        const spotlightUserMessage: SpotlightUserMessage = {
            kind: "spotlightUser",
            userId: isSpotlighted ? null : userId,
        };
        roomState.spotlightUserId = spotlightUserMessage.userId;

        for (const layout of roomState.videoLayout) {
            room.sendMessage(spotlightUserMessage, layout.userId);
        }
    };

    @action
    private handleTargetInteraction = (nextOpenState: boolean) => {
        this.props.layout.isDropdownOpen = nextOpenState;
    };

    @action
    private handleRemoteParticipantPin = () => {
        const { roomState, layout, currentNumCols } = this.props;
        const { isPinned } = layout;

        if (isPinned) {
            layout.gridX = undefined;
            layout.gridY = undefined;
            layout.isPinned = false;
            shiftLayoutsAfterUnpinning(roomState.videoLayout, layout, currentNumCols);
        } else {
            layout.gridX = 0;
            layout.gridY = 0;
            layout.isPinned = true;
            shiftLayoutsAfterPinning(roomState.videoLayout, layout, currentNumCols);
        }
    };

    @action
    private handleResetVideoSize = () => {
        this.props.layout.gridWidth = 1;
        this.props.layout.gridHeight = 1;
    };

    @action
    private handleRequestPrivateChat = () => {
        const { userId, room } = this.props;
        const dataMessage: DataMessage = { kind: "chatRequest" };
        this.chatRequestStatus = "outgoingRequest";
        room.sendMessage(dataMessage, userId);
    };

    @action
    private handleBlockPrivateChat = () => {
        this.showBlockConfirmDialog = true;
    };

    @action
    private handleAcceptPrivateChat = () => {
        const { userId, room, roomState } = this.props;
        ChatRequestManager.handleAcceptChatRequest(userId, room, roomState);
    };

    @action
    private handleBlockConfirm = () => {
        const { userId, room } = this.props;
        const dataMessage: DataMessage = { kind: "chatBlock" };
        this.chatRequestStatus = "outgoingBlock";
        room.sendMessage(dataMessage, userId);
        this.showBlockConfirmDialog = false;
    };

    @action
    private handleBlockCancel = () => {
        this.showBlockConfirmDialog = false;
    };
}
